import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { TokenContext } from '@/Contexts/TokenContext';
import { FuzzyMatchResultsProps } from '@/models/FuzzyMatchResults';
import { PartnerPatchPayload, PartnerProps } from '@/models/Partner';
import { PartnerDashboardDetailsProps } from '@/models/PartnerDashboard';
import { PartnerGroupProps } from '@/models/PartnerGroup';
import { PartnerGroupSelectModel } from '@/models/PartnerGroupSelectModel';
import { ProgramNames } from '@/models/Program';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';

export default function usePartner() {
  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();
  const navigate = useNavigate();

  const partnerUrl = useMemo(() => `${baseUrl}partners`, []);

  const getPartner = useCallback(
    async (partnerId: number): Promise<PartnerProps> => {
      try {
        const response = await customFetch<PartnerProps>(`${partnerUrl}/${partnerId}`, {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        });

        return response;
      } catch {
        navigate('/error');
        throw new Error(`Could not get partner with id ${partnerId}`);
      }
    },
    [authResult, customFetch, navigate, partnerUrl]
  );

  const getPartnerSimple = useCallback(
    async (
      programId: ProgramNames,
      stateId: string | null = null,
      countyId: string | null = null,
      countryId: number | null = null
    ) => {
      let url;
      let allPartners: PartnerProps[] = [];
      if (countryId != null) {
        url = `${partnerUrl}/simple?programId=${programId}&countryid=${countryId}&page=1&itemsperpage=100000`;
      } else if (stateId != null && countyId == null) {
        url = `${partnerUrl}/simple?programId=${programId}&stateid=${stateId}&page=1&itemsperpage=100000`;
      } else {
        url = `${partnerUrl}/simple?programId=${programId}&stateid=${stateId}&countyid=${countyId}&page=1&itemsperpage=100000`;
      }

      if (countryId == null && stateId == null && countyId == null) {
        url = `${partnerUrl}/simple?programId=${programId}&page=1&itemsperpage=100000`;
      }

      allPartners = await customFetch<PartnerProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return allPartners;
    },
    [authResult, customFetch, partnerUrl]
  );

  const getPartnersByProgram = useCallback(
    async (programId: number, abortController: AbortController) => {
      try {
        const response = await customFetch<PartnerProps[]>(`${partnerUrl}?programId=${programId}`, {
          method: 'GET',
          signal: abortController.signal,
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        });

        return response;
      } catch {
        // TODO(bug): this also runs when the caller aborts the request — ManagePartners aborts on
        // program change/unmount — so switching program (or leaving the page) mid-fetch sends the
        // user to /error. Expected: if abortController.signal.aborted, rethrow without navigating.
        navigate('/error');
        throw new Error(`Could not get partners for program ${programId}`);
      }
    },
    [authResult, customFetch, navigate, partnerUrl]
  );

  const getPartnersByCountyId = async (
    programId: ProgramNames,
    stateId: string,
    countyId: string
  ) => {
    const allPartners = await customFetch(
      `${partnerUrl}?programid=${programId}&stateid=${stateId}&countyid=${countyId}`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      }
    );

    return allPartners;
  };

  const getPartnerGroup = useCallback(
    async (groupId: number): Promise<PartnerGroupProps> => {
      const response = await customFetch<PartnerGroupProps>(`${baseUrl}partnergroups/${groupId}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getPartnersByPartnerGroupId = useCallback(
    async (groupId: number): Promise<PartnerProps[]> => {
      const response = await customFetch<PartnerProps[]>(
        `${baseUrl}partners/PartnersByPartnerGroup?partnerGroupId=${groupId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        }
      );

      return response;
    },
    [authResult, customFetch]
  );

  const getPartnerGroupSelectOptions = useCallback(async (): Promise<PartnerGroupSelectModel[]> => {
    const response = await customFetch<PartnerGroupSelectModel[]>(
      `${baseUrl}PartnerGroups/PartnerGroupSelectOptions`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      }
    );

    return response;
  }, [authResult, customFetch]);

  const postPartner = useCallback(
    async (postData: PartnerProps) => {
      const updatedPartner = await customFetch<PartnerProps>(partnerUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(postData),
      });

      return updatedPartner;
    },
    [authResult, customFetch, partnerUrl]
  );

  const patchPartnerRecord = useCallback(
    async (partnerId: number, patchData: PartnerPatchPayload) => {
      const updatedPartner = await customFetch<PartnerProps>(`${partnerUrl}/${partnerId}`, {
        method: 'PATCH',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(patchData),
      });

      return updatedPartner;
    },
    [authResult, customFetch, partnerUrl]
  );

  const postPartnersMerge = async (postData: unknown) => {
    const url = `${partnerUrl}/merge`;
    const response = await customFetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
      body: JSON.stringify(postData),
    });
    return response;
  };

  const getPartnerNameFuzzy = useCallback(
    async (
      name: string,
      programId: number,
      stateId: string | null | undefined,
      countryId: number | null | undefined
    ) => {
      // TODO(bug): `name` is not URL-encoded, so partner names containing & # + % (e.g.
      // "Town & Country") are truncated/corrupted and the fuzzy match searches the wrong string.
      // Expected: encodeURIComponent(name) (or build with URLSearchParams).
      const url = `${partnerUrl}/partnername?name=${name}&programid=${programId}&stateid=${stateId}&countryid=${countryId}`;
      const response = await customFetch<FuzzyMatchResultsProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch, partnerUrl]
  );

  const getPartnerDataGridDetails = useCallback(
    async (partnerId: number, programId: number): Promise<PartnerDashboardDetailsProps> => {
      const response = await customFetch<PartnerDashboardDetailsProps>(
        `${partnerUrl}/datagriddetails/${partnerId}?programId=${programId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        }
      );

      return response;
    },
    [authResult, customFetch, partnerUrl]
  );

  const deletePartner = async (partnerId: number): Promise<void> => {
    const url = `${partnerUrl}/${partnerId}`;
    await customFetch(url, {
      method: 'DELETE',
      headers: {
        accept: '*/*',
        Authorization: `Bearer ${authResult.accessToken}`,
        'Cache-Control': 'no-cache',
      },
    });
  };

  const getAdvancedPartnerExport = useCallback(
    async (program: ProgramNames): Promise<void> => {
      const url = `${partnerUrl}/AdvancedPartnerExport?programId=${program}`;
      await customFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
          'Cache-Control': 'no-cache',
        },
      });
    },
    [authResult, customFetch, partnerUrl]
  );

  return {
    getPartner,
    getPartnerSimple,
    postPartner,
    patchPartnerRecord,
    getPartnerGroup,
    getPartnersByCountyId,
    getPartnersByProgram,
    postPartnersMerge,
    getPartnerNameFuzzy,
    deletePartner,
    getPartnerGroupSelectOptions,
    getPartnerDataGridDetails,
    getAdvancedPartnerExport,
    getPartnersByPartnerGroupId,
  };
}
